import type { CSSProperties, FC } from "react";
import { useEffect, useState } from "react";

import { useBlocNext } from "@blac/react";
import {
  AddMealInsightFeedbackRequest,
  MealInsightHistoryResponse
} from "@9amhealth/openapi";
import { Modal } from "@9amhealth/shared";
import type { ZonedDateTime } from "@internationalized/date";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { fileState } from "src/state/state";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import Translate from "src/ui/components/Translate/Translate";

import Feedback from "./Feedback";
import MealInsightActionButton from "./MealInsightActionButton";
import MealInsightContent from "./MealInsightContent";
import MealTakenAtEditor from "./MealTakenAtEditor";
import {
  mealTakenAtAsZoned,
  toOffsetDateTimeString
} from "./mealInsightHelpers";

const PREVIEW_MAX_WIDTH = 500;

const FEEDBACK_TO_RATING: Record<
  MealInsightHistoryResponse.latestFeedback,
  AddMealInsightFeedbackRequest.rating
> = {
  [MealInsightHistoryResponse.latestFeedback.GOOD]:
    AddMealInsightFeedbackRequest.rating.GOOD,
  [MealInsightHistoryResponse.latestFeedback.BAD]:
    AddMealInsightFeedbackRequest.rating.BAD,
  [MealInsightHistoryResponse.latestFeedback.REMOVED]:
    AddMealInsightFeedbackRequest.rating.REMOVED
};

const MODAL_STYLE = {
  "--dialog-background": "var(--color-cream, #fffcf3)",
  "--width": "100vw",
  "--max-width": "100vw",
  "--height": "100dvh",
  "--max-height": "100dvh",
  "--border-radius": "0"
} as CSSProperties;

interface MealInsightDialogProps {
  meal: MealInsightHistoryResponse;
  onClose: () => void;
}

const MealInsightDialog: FC<MealInsightDialogProps> = ({ meal, onClose }) => {
  const [{ meals }, { deleteMeal, rateMeal, updateMealTakenAt }] =
    useBlocNext(MealInsightsCubit);
  const [, { invalidate: invalidateMealStats }] = useBlocNext(MealsStatsCubit);
  const [isOpen, setIsOpen] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [takenAtDialogOpen, setTakenAtDialogOpen] = useState(false);

  // The `meal` prop is the snapshot the list handed over on click, so an edited
  // date would otherwise keep reading the old value until the dialog is
  // reopened. Fall back to that snapshot once a delete drops the meal from the
  // cubit, so the header stays intact through the dismiss animation.
  const takenAt =
    meals.find((entry) => entry.id === meal.id)?.takenAt ?? meal.takenAt;

  useEffect(() => {
    let active = true;
    const windowWidth = window.innerWidth;
    const density = window.devicePixelRatio;
    const width = Math.round(
      Math.min(windowWidth, PREVIEW_MAX_WIDTH) * density
    );
    void fileState.loadPreview(meal.fileId, width, width).then((url) => {
      if (active && url) {
        setImageUrl(url);
      }
    });
    return () => {
      active = false;
    };
  }, [meal.fileId]);

  useEffect(() => {
    if (!imageUrl) {
      return;
    }
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = imageUrl;
  }, [imageUrl]);

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleTakenAtSaved = (picked: ZonedDateTime): void => {
    if (picked.compare(mealTakenAtAsZoned(takenAt)) === 0) {
      return;
    }
    void updateMealTakenAt(meal.id, toOffsetDateTimeString(picked)).then(
      (updated) => {
        if (updated) {
          invalidateMealStats();
        }
      }
    );
  };

  const handleConfirmDelete = async (): Promise<void> => {
    const deleted = await deleteMeal(meal.id);
    if (deleted) {
      invalidateMealStats();
      handleClose();
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        variant="center"
        style={MODAL_STYLE}
        onWillDismiss={handleClose}
        onDidDismiss={onClose}
      >
        <MealInsightContent
          imageUrl={imageUrl}
          imageLoaded={imageLoaded}
          loading={false}
          mealIdentified={meal.mealIdentified}
          mealName={meal.mealName}
          takenAt={takenAt}
          onEditTakenAt={() => setTakenAtDialogOpen(true)}
          caloriesRange={meal.nutrients?.caloriesRange}
          insightsHtml={meal.insights}
          nutrients={meal.nutrients}
          showCloseButton
          closeContext="meals"
          onClose={handleClose}
          stickyAction={false}
          feedbackSlot={
            <Feedback
              initialRating={
                meal.latestFeedback
                  ? FEEDBACK_TO_RATING[meal.latestFeedback]
                  : null
              }
              onRate={(rating) => void rateMeal(meal.id, rating)}
            />
          }
          actionSlot={
            <MealInsightActionButton
              label={<Translate msg="mealInsights.delete" />}
              destructive
              onClick={() => setConfirmDeleteOpen(true)}
            />
          }
        />
      </Modal>
      {takenAtDialogOpen && (
        <MealTakenAtEditor
          takenAt={takenAt}
          onClose={() => setTakenAtDialogOpen(false)}
          onSave={handleTakenAtSaved}
        />
      )}
      {confirmDeleteOpen && (
        <AppDialog
          confirmText="mealInsights.delete.confirm"
          confirmArrow={false}
          destructive
          hideClose
          cancelText="mealInsights.delete.no"
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => void handleConfirmDelete()}
        >
          <h5>
            <Translate msg="mealInsights.delete.confirmTitle" />
          </h5>
          <p>
            <Translate msg="mealInsights.delete.confirmBody" />
          </p>
        </AppDialog>
      )}
    </>
  );
};

export default MealInsightDialog;
